import { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Prompt, TrayOptions, Typography, useTray } from '@weareopenr/component-library';

/** What the menu page's tray is currently holding. */
export type MenuTrayKind =
  | 'preview'
  | 'move-product'
  | 'edit-section'
  | 'section-availability'
  | 'add-products'
  | 'edit-menu-set-details'
  | 'duplicate-menu-set'
  | 'validate';

export interface MenuTrayOptions extends TrayOptions {
  /** Identifies what opened the tray, so a caller can tell whether it still owns it. */
  owner?: string;
}

interface PendingTray {
  kind: MenuTrayKind;
  content: ReactNode;
  options: MenuTrayOptions;
}

export interface MenuTrayValue {
  /** Opens the shared tray, recording what it now holds and who opened it. */
  openTray: (kind: MenuTrayKind, content: ReactNode, options?: MenuTrayOptions) => void;
  closeTray: () => void;
  isTrayOpen: boolean;
  /** Null while the tray is closed. */
  trayKind: MenuTrayKind | null;
  /** True when the open tray was opened by this owner. */
  isTrayOwnedBy: (owner: string) => boolean;
  /** Reported by tray content so the tray is not replaced while it holds unsaved work. */
  setTrayDirty: (isDirty: boolean) => void;
}

const MenuTrayContext = createContext<MenuTrayValue | undefined>(undefined);

/**
 * The shared tray holds one thing at a time and reports only whether it is open, so a
 * caller can tell neither what it would be replacing nor whether it still owns what is
 * there. This records both alongside it.
 *
 * Every open sets the kind and owner, so a new caller has nothing to remember to clear,
 * and both derive from `isTrayOpen`, so every close path resets them.
 */
export const MenuTrayProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const { openTray: openSharedTray, closeTray, isTrayOpen } = useTray();
  const [openedKind, setOpenedKind] = useState<MenuTrayKind | null>(null);
  const [openedBy, setOpenedBy] = useState<string | null>(null);
  const [pendingTray, setPendingTray] = useState<PendingTray | null>(null);
  // Only read when a tray is about to be replaced, so it never needs to trigger a render.
  const isDirtyRef = useRef(false);

  const setTrayDirty = useCallback((isDirty: boolean) => {
    isDirtyRef.current = isDirty;
  }, []);

  const replaceTray = useCallback(
    ({ kind, content, options }: PendingTray) => {
      isDirtyRef.current = false;
      setOpenedKind(kind);
      setOpenedBy(options.owner ?? null);
      openSharedTray(content, {
        ...options,
        onClose: () => {
          isDirtyRef.current = false;
          setOpenedKind(null);
          setOpenedBy(null);
          options.onClose?.();
        },
      });
    },
    [openSharedTray],
  );

  const openTray = useCallback(
    (kind: MenuTrayKind, content: ReactNode, options: MenuTrayOptions = {}) => {
      // Replacing the tray unmounts whatever is in it, so unsaved work needs confirming first.
      if (isDirtyRef.current) {
        setPendingTray({ kind, content, options });
        return;
      }

      replaceTray({ kind, content, options });
    },
    [replaceTray],
  );

  const discardAndOpenPending = useCallback(() => {
    if (pendingTray) replaceTray(pendingTray);
    setPendingTray(null);
  }, [pendingTray, replaceTray]);

  const trayKind = isTrayOpen ? openedKind : null;
  const trayOwner = isTrayOpen ? openedBy : null;

  const isTrayOwnedBy = useCallback((owner: string) => trayOwner === owner, [trayOwner]);

  const value = useMemo<MenuTrayValue>(
    () => ({ openTray, closeTray, isTrayOpen, trayKind, isTrayOwnedBy, setTrayDirty }),
    [openTray, closeTray, isTrayOpen, trayKind, isTrayOwnedBy, setTrayDirty],
  );

  return (
    <MenuTrayContext.Provider value={value}>
      {children}
      <Prompt
        promptHeading="Discard unsaved changes?"
        open={!!pendingTray}
        closePrompt={() => setPendingTray(null)}
        buttonHandler={discardAndOpenPending}
        buttonText="Discard changes"
        buttonColour="tomato"
        backButton
        backButtonText="Keep editing"
        size="small"
      >
        <Typography size="medium">The panel you are working in has changes that have not been saved yet.</Typography>
      </Prompt>
    </MenuTrayContext.Provider>
  );
};

export const useMenuTray = (): MenuTrayValue => {
  const value = useContext(MenuTrayContext);

  if (!value) {
    throw new Error('useMenuTray must be used within a MenuTrayProvider');
  }

  return value;
};
